import { useApiClient } from "@/src/hooks/useApiClient";
import { useQueryErrorToast } from "@/src/hooks/useQueryErrorToast";
import { useTranslations } from "@/src/i18n/i18n";

/** Cinquante commentaires par page — la borne du serveur par défaut. */
export const COMMENTS_PAGE_SIZE = 50;

/**
 * Le fil de commentaires d'une publication, vu par la modération.
 *
 * Trois écarts avec ce que lit l'application, et chacun a sa raison :
 *
 * 1. ⚠️ **le contenu d'un commentaire supprimé est conservé** — la suppression
 *    est le plus souvent la suite donnée à un signalement, la masquer effacerait
 *    la preuve. Même parti que la lecture d'une conversation (`ConversationViewer`),
 *    d'où le barré à l'écran et non l'absence ;
 * 2. **l'auteur reste attribué**, y compris sur un commentaire supprimé : c'est
 *    lui, et pas le texte, qui décide de la sanction ;
 * 3. **la liste est plate**, `parentCommentId` à l'appui — c'est l'écran qui
 *    imbrique. Paginer un arbre ferait manquer une réponse dès que sa racine
 *    tombe sur la page suivante.
 *
 * ⚠️ **Pas de journalisation ici**, contrairement aux conversations privées :
 * un commentaire est du contenu public, le serveur n'inscrit rien au registre.
 * Le fil peut donc se charger avec la fiche, sans attendre un clic.
 */
export function usePostComments(postId: number, page: number) {
  const $api = useApiClient();
  const t = useTranslations("posts");

  const { data, error, isError, isLoading } = $api.useQuery(
    "get",
    "/api/v1/moderation/post/{id}/comment",
    {
      params: {
        path: { id: postId },
        query: {
          limit: COMMENTS_PAGE_SIZE,
          offset: (page - 1) * COMMENTS_PAGE_SIZE,
        },
      },
    },
    { enabled: postId > 0 },
  );

  useQueryErrorToast(isError, t("comments-error"), error);

  const total = data?.data?.total ?? 0;

  return {
    comments: data?.data?.comments ?? [],
    total,
    pageCount: Math.max(1, Math.ceil(total / COMMENTS_PAGE_SIZE)),
    isLoading,
    isError,
  };
}
